/**
 * Get the base path from vite.config.ts
 * Returns the base URL without trailing slash (e.g., '/idp')
 */
export const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, '');
/**
 * Helper to create full path with base URL from vite.config.ts
 * @param path - The path to append to base (e.g., '/unauthorized')
 * @returns Full path with base (e.g., '/idp/unauthorized')
 *
 * @example
 * makeFullPath('/unauthorized') // Returns '/idp/unauthorized'
 * makeFullPath('login') // Returns '/idp/login'
 */
export function makeFullPath(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${BASE_PATH}${cleanPath}`;
}

/**
 * Build a user-portal URL for the current tenant.
 * Web-login runs on {tenant}.{domain}, user-portal runs on app.{domain}/{tenant}.
 * @param path - Path within user-portal (e.g., '/dashboard', '/login')
 * @returns Full URL like https://app.spark.dev/acme/dashboard
 */
export function getUserPortalUrl(path: string): string {
  const { protocol, hostname, port } = window.location;
  const dotIndex = hostname.indexOf('.');

  if (dotIndex === -1) {
    return path;
  }

  const tenant = hostname.substring(0, dotIndex);
  const baseDomain = hostname.substring(dotIndex + 1);
  const portSuffix = port ? `:${port}` : '';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${protocol}//app.${baseDomain}${portSuffix}/${tenant}${cleanPath}`;
}

/**
 * Helper to create asset path with base URL from vite.config.ts
 * For assets in the public folder
 * @param path - The asset path (e.g., 'logo.svg' or '/logo.svg')
 * @returns Full asset path with base (e.g., '/idp/logo.svg')
 *
 * @example
 * publicAsset('logo.svg') // Returns '/idp/logo.svg'
 * publicAsset('/error-image.svg') // Returns '/idp/error-image.svg'
 */
export function publicAsset(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${BASE_PATH}${cleanPath}`;
}

/**
 * Web-login runs at <tenant>.<domain>. Returns the leading subdomain
 * ('' if the hostname has no dot, e.g. localhost).
 */
export function getTenantSlugFromHostname(): string {
  const dotIndex = window.location.hostname.indexOf('.');

  return dotIndex === -1 ? '' : window.location.hostname.substring(0, dotIndex);
}
